import { makeAutoObservable, runInAction } from 'mobx';
import { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { TFunction } from '@wix/yoshi-flow-editor';
import type { AvailabilityStatusKeysConfig } from 'root/availabilityStatusKeys';
import type { TimeSlot, DispatchInfo } from 'root/types/businessTypes';
import { getAvailabilityStatusText, initializeAvailability } from 'root/utils/availabilityUtils';
import type { CommonAvailability } from 'root/types/availability';

export class AvailabilityState {
  private availabilityStatus?: AvailabilityStatus;
  private availability?: CommonAvailability;
  private nextAvailableTimeslot?: boolean;

  constructor(initialAvailability?: CommonAvailability) {
    this.availability = initialAvailability;
    makeAutoObservable(this);
  }

  get isAvailable() {
    return this.availabilityStatus !== AvailabilityStatus.UNAVAILABLE;
  }

  get currentAvailability(): CommonAvailability | undefined {
    return this.availability;
  }

  get hasNextAvailableTimeslot() {
    return this.nextAvailableTimeslot;
  }

  updateAvailabilityStatus(availabilityStatus: AvailabilityStatus) {
    runInAction(() => {
      this.availabilityStatus = availabilityStatus;
    });
  }

  updateAvailability(availability: CommonAvailability) {
    runInAction(() => {
      this.availability = availability;
    });
  }

  updateNextAvailableTimeslot(hasNextAvailableTimeslot: boolean) {
    runInAction(() => {
      this.nextAvailableTimeslot = hasNextAvailableTimeslot;
    });
  }

  initializeAvailability(
    selectedTimeSlot?: TimeSlot,
    timezone?: string,
    businessDaysAhead?: number
  ) {
    initializeAvailability(
      this.availability as CommonAvailability,
      selectedTimeSlot,
      timezone,
      this.updateAvailabilityStatus.bind(this),
      this.updateNextAvailableTimeslot.bind(this),
      businessDaysAhead
    );
  }

  updateAvailabilityForDispatch(dispatchInfo: DispatchInfo) {
    this.initializeAvailability(dispatchInfo.selectedTimeSlot);
  }

  getAvailabilityStatus({
    locale,
    timezone,
    t,
    keys,
  }: {
    locale: string;
    timezone: string;
    t: TFunction;
    keys: AvailabilityStatusKeysConfig;
  }) {
    const availability = this.currentAvailability;
    if (!availability) {
      return { shouldCollapseAvailabilityStatus: true, text: '' };
    }

    return getAvailabilityStatusText(
      {
        hasNextAvailableTimeslot: this.hasNextAvailableTimeslot,
        availability,
      },
      locale,
      timezone,
      t,
      keys
    );
  }
}
